import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings2, Send, X } from "lucide-react";

export default function PulseAlertsSettingsDialog({ wsId, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/brands/pulse/alerts/settings`)
      .then((r) => setCfg(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load settings")));
  }, [wsId]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const addEmail = () => {
    const e = (emailInput || "").trim();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email"); return;
    }
    const list = cfg?.digest_recipients || [];
    if (list.includes(e)) return;
    set("digest_recipients", [...list, e]);
    setEmailInput("");
  };
  const removeEmail = (e) => set("digest_recipients", (cfg?.digest_recipients || []).filter((x) => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/brands/pulse/alerts/settings`, {
        slack_webhook_url: cfg.slack_webhook_url || null,
        digest_enabled: !!cfg.digest_enabled,
        digest_recipients: cfg.digest_recipients || [],
        big_shift_notifications: !!cfg.big_shift_notifications,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/brands/pulse/alerts/notify?force=true`);
      toast.success(
        `Fired ${r.data.triggered_count} alert(s) · ${r.data.emails_sent} email(s), ${r.data.slack_sent} slack post(s)`
      );
    } catch (e) { toast.error(apiErrorMessage(e, "Test send failed")); }
    finally { setTesting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="pulse-alerts-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-primary" />
            Pulse alerts · Workspace settings
          </DialogTitle>
        </DialogHeader>
        {!cfg ? (
          <div className="text-xs text-muted-foreground py-4">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Slack webhook URL</Label>
              <Input value={cfg.slack_webhook_url || ""}
                       onChange={(e) => set("slack_webhook_url", e.target.value)}
                       placeholder="https://hooks.slack.com/services/…"
                       className="h-9 rounded-none font-mono text-xs"
                       data-testid="pulse-alerts-slack-url" />
              <div className="text-[10px] text-muted-foreground mt-1">
                Portfolio alerts + Monday digest post to this channel. Leave empty to disable Slack.
              </div>
            </div>

            <div className="flex items-center justify-between border border-border p-3 bg-card">
              <div>
                <div className="text-sm font-semibold">Weekly digest (Mondays)</div>
                <div className="text-[10px] text-muted-foreground">
                  Every Monday 08:45 UTC, we sweep all brands and email a digest of currently-firing alerts.
                </div>
              </div>
              <Switch checked={!!cfg.digest_enabled}
                        onCheckedChange={(v) => set("digest_enabled", v)}
                        data-testid="pulse-alerts-digest-enabled" />
            </div>

            <div className="flex items-center justify-between border border-border p-3 bg-card">
              <div>
                <div className="text-sm font-semibold">Big shift alerts (AI-detected)</div>
                <div className="text-[10px] text-muted-foreground">
                  When Snapshot Autopilot detects a material brand-vs-competitor shift
                  (AI confidence ≥ 70 with regressions or ≥3 substantive bullets),
                  fire a dedicated email + Slack alert to the same recipients.
                </div>
              </div>
              <Switch checked={!!cfg.big_shift_notifications}
                        onCheckedChange={(v) => set("big_shift_notifications", v)}
                        data-testid="pulse-alerts-big-shift-enabled" />
            </div>

            <div>
              <Label className="label-eyebrow mb-1 inline-block">Digest recipients</Label>
              <div className="flex gap-2">
                <Input value={emailInput}
                         onChange={(e) => setEmailInput(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                         placeholder="team@company.com"
                         className="h-9 rounded-none flex-1"
                         data-testid="pulse-alerts-digest-email-input" />
                <Button variant="outline" className="rounded-none h-9" onClick={addEmail}
                          data-testid="pulse-alerts-digest-email-add-btn">Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2" data-testid="pulse-alerts-digest-email-list">
                {(cfg.digest_recipients || []).map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 text-[10px] border border-border bg-muted/40 px-1.5 py-0.5">
                    {e}
                    <button type="button" onClick={() => removeEmail(e)} className="hover:text-rose-600">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                {!(cfg.digest_recipients || []).length && (
                  <span className="text-[10px] text-muted-foreground italic">
                    Falls back to Founder Brief recipients if empty.
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="flex items-center gap-2">
          <Button variant="outline" className="rounded-none" onClick={sendTest} disabled={testing || !cfg}
                    data-testid="pulse-alerts-test-send-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {testing ? "Sending…" : "Send now (test)"}
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving || !cfg}
                    data-testid="pulse-alerts-settings-save-btn">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
